import Swiper from "swiper";
import { A11y, Keyboard, Mousewheel, Pagination } from "swiper/modules";
import { initHeroScrollHint } from "./init-hero-scroll-hint";
import { initSwiperNestedScroll } from "./init-swiper-nested-scroll";
import {
	EVENTS_SECTION,
	LOCATIONS_SECTION,
	normalizeHashId,
	parsePageHash,
} from "./page-hash";

function getNavSections(root) {
	const nav = root.querySelector(".fullpage__side-nav");
	if (!nav) {
		return [];
	}

	return Array.from(nav.querySelectorAll("[data-fullpage-tab]"))
		.map((tab) => tab.getAttribute("data-fullpage-tab"))
		.filter(Boolean);
}
const NAV_EDGE_FALLBACK_PX = 24;
const NAV_GAP_PX = 8;
const MOBILE_NAV_GAP_PX = 20;
const MOBILE_LAYOUT_MQ = "(max-width: 599px)";
// Locations switch to the stacked (photo on top) layout below this width.
// Keep in sync with $bp-locations-split in _variables.scss.
const LOCATIONS_STACKED_MQ = "(max-width: 899px)";

function getGridMarginPx() {
	const raw = getComputedStyle(document.documentElement)
		.getPropertyValue("--paaadel-grid-margin")
		.trim();
	const px = parseFloat(raw);

	return Number.isFinite(px) && px > 0 ? px : NAV_EDGE_FALLBACK_PX;
}

function getAdminBarOffsetPx() {
	if (!document.body.classList.contains("admin-bar")) {
		return 0;
	}

	const bar = document.getElementById("wpadminbar");
	return bar ? Math.round(bar.getBoundingClientRect().height) : 0;
}

function getViewportHeight() {
	// iOS Safari: layout viewport lets browser chrome collapse; visualViewport
	// letterboxes and leaves theme background visible at top/bottom.
	if (isMobileLayout()) {
		return Math.round(window.innerHeight);
	}

	const visual = window.visualViewport;
	return Math.round(
		visual && visual.height ? visual.height : window.innerHeight,
	);
}

function applySlideHeights(swiperEl, height) {
	const px = `${height}px`;

	if (swiperEl) {
		swiperEl.style.height = px;
	}

	const slides = swiperEl?.querySelectorAll(".swiper-slide");
	if (!slides) {
		return;
	}

	slides.forEach((slide) => {
		slide.style.height = px;
		slide.style.minHeight = px;
		slide.style.maxHeight = px;
	});
}

function snapSwiperToIndex(swiper, index, speed = 0) {
	const height = getViewportHeight();
	applySlideHeights(swiper.el, height);
	swiper.params.height = height;
	swiper.update();
	swiper.slideTo(index, speed);

	if (speed === 0) {
		swiper.setTranslate(-index * height);
		swiper.updateProgress();
		swiper.updateActiveIndex();
	}
}

function syncFullpageViewport(root, swiperEl, swiper) {
	const height = getViewportHeight();
	const px = `${height}px`;

	root.style.setProperty("--fullpage-vh", px);
	document.documentElement.style.setProperty("--fullpage-vh", px);
	applySlideHeights(swiperEl, height);

	if (swiper && !swiper.destroyed) {
		swiper.params.height = height;
		swiper.update();
		snapSwiperToIndex(swiper, swiper.activeIndex, 0);
	}
}

function slideIndexForSection(root, swiper, section) {
	const target = root.querySelector(
		`[data-fullpage-section="${section}"], #${section}, #section-${section}`,
	);
	if (!target) {
		return -1;
	}

	const slides = Array.from(swiper.slides);
	return slides.indexOf(target);
}

/** Hero + media slides use plain page URL (no hash). */
function slideUsesCleanUrl(slide) {
	if (!slide) {
		return false;
	}

	return (
		slide.classList.contains("fullpage-slide--hero") ||
		slide.classList.contains("fullpage-slide--media")
	);
}

function findSlideIndexByHashId(slides, hash, { fallback = -1 } = {}) {
	const { section } = parsePageHash(hash);
	if (!section) {
		return slides.length > 0 ? 0 : fallback;
	}

	const normalized = section;

	if (normalized === "hero") {
		return 0;
	}

	if (normalized === "media") {
		const mediaIndex = slides.findIndex((slide) =>
			slide.classList.contains("fullpage-slide--media"),
		);
		return mediaIndex >= 0 ? mediaIndex : fallback;
	}

	const index = slides.findIndex((slide) => {
		if (slide.id === normalized) {
			return true;
		}

		const slideSection = slide.getAttribute("data-fullpage-section");
		return slideSection === normalized;
	});

	return index >= 0 ? index : fallback;
}

function hashForSlide(slide) {
	if (!slide || slideUsesCleanUrl(slide)) {
		return "";
	}

	const section = slide.getAttribute("data-fullpage-section");
	if (section) {
		return `#${section}`;
	}

	if (slide.id) {
		return `#${slide.id}`;
	}

	return "";
}

function indexForHash(swiper, hash) {
	return findSlideIndexByHashId(Array.from(swiper.slides), hash, {
		fallback: -1,
	});
}

function initialSlideIndex(swiperEl, hash) {
	const wrapper = swiperEl.querySelector(".swiper-wrapper");
	if (!wrapper) {
		return 0;
	}

	return findSlideIndexByHashId(Array.from(wrapper.children), hash, {
		fallback: 0,
	});
}

function isMobileLayout() {
	return window.matchMedia(MOBILE_LAYOUT_MQ).matches;
}

function prefersFinePointer() {
	return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

const SCROLL_NO_SWIPE_SELECTOR = [
	".fullpage-slide__text-body",
	".fullpage-slide__locations-textbox",
	"[data-events-calendar] .fullpage-events-calendar__panels",
	"[data-events-calendar] .fullpage-events-calendar__nav",
	"[data-events-calendar] .fullpage-events-calendar__mobile-nav",
	"[data-events-calendar] [data-mobile-strip-scroll]",
	"[data-events-calendar] [data-mobile-days-scroll]",
].join(", ");

function syncSlideInnerSwiping(root) {
	const lockInnerSwipe = prefersFinePointer();
	const mobile = isMobileLayout();

	root.querySelectorAll(".fullpage-slide__inner").forEach((el) => {
		el.classList.toggle("swiper-no-swiping", lockInnerSwipe && !mobile);
	});

	// Clear stale no-swipe flags before re-applying.
	root.querySelectorAll(
		`${SCROLL_NO_SWIPE_SELECTOR}, [data-mobile-days-scroll]`,
	).forEach((el) => {
		el.classList.remove("swiper-no-swiping");
	});

	if (mobile) {
		// Mobile: only horizontal day strips block Swiper (native pan-x scroll).
		root.querySelectorAll("[data-mobile-days-scroll]").forEach((el) => {
			el.classList.add("swiper-no-swiping");
		});
		return;
	}

	root.querySelectorAll(SCROLL_NO_SWIPE_SELECTOR).forEach((el) => {
		el.classList.add("swiper-no-swiping");
	});
}

function getMobileMenuEl(root) {
	return root.querySelector("[data-fullpage-mobile-menu]");
}

function isHeroSlideActive(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(slide?.classList.contains("fullpage-slide--hero"));
}

function usesMobileMenuLogo(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(slide?.classList.contains("fullpage-slide--hero"));
}

function usesMobileMenuCenteredPill(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(
		slide?.classList.contains("fullpage-slide--media") ||
		slide?.classList.contains("fullpage-slide--footer"),
	);
}

function getCachedMobileNavReserve(root) {
	const raw = root.dataset.mobileNavReserveClosed;
	const px = parseInt(raw || "", 10);

	return Number.isFinite(px) && px > 0 ? px : null;
}

function applyMobileNavReserve(root, px) {
	if (!Number.isFinite(px) || px <= 0) {
		return;
	}

	root.style.setProperty(
		"--paaadel-mobile-nav-reserve",
		`${Math.ceil(px)}px`,
	);
	root.dataset.mobileNavReserveClosed = String(Math.ceil(px));
}

function isMobileMenuPanelCollapsed(menu) {
	const panel = menu.querySelector("[data-fullpage-mobile-menu-panel]");
	if (!panel) {
		return true;
	}

	return panel.getBoundingClientRect().height <= 4;
}

function scheduleMobileNavReserveRemeasure(root) {
	const menu = getMobileMenuEl(root);
	const panel = menu?.querySelector("[data-fullpage-mobile-menu-panel]");
	if (!panel) {
		updateMobileNavReserve(root);
		return;
	}

	const onEnd = (event) => {
		if (
			event.propertyName !== "max-height" &&
			event.propertyName !== "padding"
		) {
			return;
		}

		panel.removeEventListener("transitionend", onEnd);
		updateMobileNavReserve(root);
	};

	panel.addEventListener("transitionend", onEnd);
}

function setMobileMenuOpen(root, open) {
	const menu = getMobileMenuEl(root);
	if (!menu) {
		return;
	}

	const toggle = menu.querySelector("[data-fullpage-mobile-menu-toggle]");
	const panel = menu.querySelector("[data-fullpage-mobile-menu-panel]");

	menu.classList.toggle("is-open", open);

	if (toggle) {
		toggle.setAttribute("aria-expanded", open ? "true" : "false");
		toggle.setAttribute("aria-hidden", open ? "true" : "false");
		toggle.tabIndex = open ? -1 : 0;
	}

	if (panel) {
		panel.setAttribute("aria-hidden", open ? "false" : "true");
		panel.querySelectorAll("a, button").forEach((el) => {
			el.tabIndex = open ? 0 : -1;
		});
	}

	if (!open) {
		const cached = getCachedMobileNavReserve(root);
		if (cached !== null) {
			applyMobileNavReserve(root, cached);
		}
		scheduleMobileNavReserveRemeasure(root);
	}
}

function closeMobileMenu(root) {
	setMobileMenuOpen(root, false);
}

function getActiveSlideSection(slide) {
	if (!slide) {
		return "";
	}

	if (slide.classList.contains("fullpage-slide--hero")) {
		return "hero";
	}

	const section = slide.getAttribute("data-fullpage-section");
	if (section) {
		return section;
	}

	return normalizeHashId(slide.id || "");
}

function getMobileBarLabels(root) {
	const menu = getMobileMenuEl(root);
	const raw = menu?.dataset.mobileBarLabels;
	if (!raw) {
		return {};
	}

	try {
		return JSON.parse(raw);
	} catch {
		return {};
	}
}

function updateMobileMenuBar(root, swiper) {
	const menu = getMobileMenuEl(root);
	if (!menu) {
		return;
	}

	const centeredPill = usesMobileMenuCenteredPill(swiper);
	const showLogo = usesMobileMenuLogo(swiper) && !centeredPill;
	const sectionEl = menu.querySelector("[data-fullpage-mobile-section]");

	menu.classList.toggle("fullpage__mobile-menu--hero", showLogo);
	menu.classList.toggle("fullpage__mobile-menu--centered", centeredPill);

	if (!sectionEl) {
		return;
	}

	if (showLogo || centeredPill) {
		menu.classList.remove("fullpage__mobile-menu--has-section");
		sectionEl.textContent = "";
		sectionEl.setAttribute("aria-hidden", "true");
		return;
	}

	const section = getActiveSlideSection(swiper.slides[swiper.activeIndex]);
	const labels = getMobileBarLabels(root);
	const linkLabel = menu
		.querySelector(
			`.fullpage__mobile-menu-link[data-fullpage-tab="${section}"]`,
		)
		?.textContent?.trim();
	const label = linkLabel || labels[section] || section;
	const hasSection = Boolean(label);

	menu.classList.toggle("fullpage__mobile-menu--has-section", hasSection);
	sectionEl.textContent = label;
	sectionEl.setAttribute("aria-hidden", hasSection ? "false" : "true");
}

function updateMobileNavReserve(root) {
	requestAnimationFrame(() => {
		if (!isMobileLayout()) {
			root.style.removeProperty("--paaadel-mobile-nav-reserve");
			delete root.dataset.mobileNavReserveClosed;
			return;
		}

		const menu = getMobileMenuEl(root);
		if (!menu) {
			root.style.removeProperty("--paaadel-mobile-nav-reserve");
			delete root.dataset.mobileNavReserveClosed;
			return;
		}

		// Open panel overlays — keep the last closed-pill reserve.
		if (menu.classList.contains("is-open")) {
			return;
		}

		if (!isMobileMenuPanelCollapsed(menu)) {
			const cached = getCachedMobileNavReserve(root);
			if (cached !== null) {
				applyMobileNavReserve(root, cached);
			}
			return;
		}

		const bar = menu.querySelector(".fullpage__mobile-menu-bar");
		const shell = menu.querySelector(".fullpage__mobile-menu-shell");
		const measureEl = bar || shell;
		if (!measureEl) {
			root.style.removeProperty("--paaadel-mobile-nav-reserve");
			delete root.dataset.mobileNavReserveClosed;
			return;
		}

		applyMobileNavReserve(
			root,
			measureEl.getBoundingClientRect().bottom + MOBILE_NAV_GAP_PX,
		);
	});
}

function bindMobileMenu(root, swiper, goToHash) {
	const menu = getMobileMenuEl(root);
	if (!menu) {
		return;
	}

	const toggle = menu.querySelector("[data-fullpage-mobile-menu-toggle]");
	const closeBtn = menu.querySelector("[data-fullpage-mobile-menu-close]");
	const panel = menu.querySelector("[data-fullpage-mobile-menu-panel]");

	panel?.querySelectorAll("a, button").forEach((el) => {
		el.tabIndex = -1;
	});

	menu.querySelector(".fullpage__mobile-menu-shell")?.addEventListener(
		"transitionend",
		(event) => {
			if (event.propertyName === "max-width") {
				updateMobileNavReserve(root);
			}
		},
	);

	toggle?.addEventListener("click", () => {
		setMobileMenuOpen(root, true);
	});

	closeBtn?.addEventListener("click", () => {
		closeMobileMenu(root);
	});

	menu.querySelectorAll(".fullpage__mobile-menu-link").forEach((link) => {
		link.addEventListener("click", () => {
			closeMobileMenu(root);
		});
	});

	document.addEventListener("keydown", (event) => {
		if (event.key !== "Escape" || !menu.classList.contains("is-open")) {
			return;
		}

		closeMobileMenu(root);
		toggle?.focus();
	});
}

function getNavStackTopPx() {
	const raw = getComputedStyle(document.documentElement)
		.getPropertyValue("--paaadel-nav-stack-top")
		.trim();
	const px = parseFloat(raw);

	if (Number.isFinite(px) && px >= 0) {
		return Math.round(px);
	}

	return getGridMarginPx() + getAdminBarOffsetPx();
}

function navTopPx(atTop, stackIndex, btnHeight) {
	const viewport = window.innerHeight;
	const step = btnHeight + NAV_GAP_PX;

	if (atTop) {
		return getNavStackTopPx() + stackIndex * step;
	}

	return viewport - getGridMarginPx() - btnHeight - stackIndex * step;
}

function getEventsCalendarScrollTargets(slide) {
	const calendar = slide?.querySelector("[data-events-calendar]");
	if (!calendar) {
		return [];
	}

	if (isMobileLayout()) {
		return [];
	}

	const targets = [
		calendar.querySelector(".fullpage-events-calendar__panels"),
	];
	const nav = calendar.querySelector(".fullpage-events-calendar__nav");

	if (nav && getComputedStyle(nav).display !== "none") {
		targets.unshift(nav);
	}

	return targets.filter(Boolean);
}

function getEventsCalendarScrollEl(slide, event) {
	const targets = getEventsCalendarScrollTargets(slide);
	if (targets.length === 0) {
		return null;
	}

	if (
		event &&
		Number.isFinite(event.clientX) &&
		Number.isFinite(event.clientY)
	) {
		for (const el of targets) {
			const rect = el.getBoundingClientRect();
			if (
				event.clientX >= rect.left &&
				event.clientX <= rect.right &&
				event.clientY >= rect.top &&
				event.clientY <= rect.bottom &&
				slideContentScrolls(el)
			) {
				return el;
			}
		}
	}

	return targets.find((el) => slideContentScrolls(el)) ?? null;
}

function getLocationsTextboxScrollEl(slide, event) {
	if (!slide?.classList.contains("fullpage-slide--locations")) {
		return null;
	}

	const textbox = slide.querySelector(".fullpage-slide__locations-textbox");
	if (!textbox || !slideContentScrolls(textbox)) {
		return null;
	}

	if (
		event &&
		Number.isFinite(event.clientX) &&
		Number.isFinite(event.clientY)
	) {
		const rect = textbox.getBoundingClientRect();
		if (
			event.clientX >= rect.left &&
			event.clientX <= rect.right &&
			event.clientY >= rect.top &&
			event.clientY <= rect.bottom
		) {
			return textbox;
		}

		return null;
	}

	return textbox;
}

function getTextSlideScrollEl(slide) {
	if (isMobileLayout()) {
		return slide?.querySelector(".fullpage-slide__inner") ?? null;
	}

	const textBody = slide?.querySelector(".fullpage-slide__text-body");
	if (textBody) {
		return textBody;
	}

	return slide?.querySelector(".fullpage-slide__inner") ?? null;
}

function getSlideScrollEl(slide, event) {
	const eventsScroll = getEventsCalendarScrollEl(slide, event);
	if (eventsScroll) {
		return eventsScroll;
	}

	const locationsScroll = getLocationsTextboxScrollEl(slide, event);
	if (locationsScroll) {
		return locationsScroll;
	}

	const inner = slide?.querySelector(".fullpage-slide__inner");
	if (inner?.classList.contains("fullpage-slide__inner--locations")) {
		return null;
	}

	if (
		slide?.classList.contains("fullpage-slide--text") ||
		slide?.classList.contains("fullpage-slide--about")
	) {
		return getTextSlideScrollEl(slide);
	}

	return inner ?? null;
}

function slideContentScrolls(scrollEl) {
	if (!scrollEl) {
		return false;
	}

	return scrollEl.scrollHeight > scrollEl.clientHeight + 1;
}

function shouldScrollSlideContent(scrollEl, deltaY) {
	if (!slideContentScrolls(scrollEl)) {
		return false;
	}

	const atTop = scrollEl.scrollTop <= 0;
	const atBottom =
		scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1;

	if (deltaY > 0 && !atBottom) {
		return true;
	}

	if (deltaY < 0 && !atTop) {
		return true;
	}

	return false;
}

function bindSlideInnerWheelScroll(swiperEl, swiper) {
	swiperEl.addEventListener(
		"wheel",
		(event) => {
			const scrollEl = getSlideScrollEl(
				swiper.slides[swiper.activeIndex],
				event,
			);
			if (!shouldScrollSlideContent(scrollEl, event.deltaY)) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			scrollEl.scrollTop += event.deltaY;
		},
		{ passive: false, capture: true },
	);
}

function getNavSectionSlide(root, section) {
	return (
		root.querySelector(`[data-fullpage-section="${section}"]`) ||
		root.querySelector(`#${section}`) ||
		root.querySelector(`#section-${section}`)
	);
}

/** Resolved page background for active nav label (matches slide). */
function getSlidePageBackgroundColor(slide) {
	if (!slide) {
		return "";
	}

	const { backgroundColor } = getComputedStyle(slide);

	if (
		!backgroundColor ||
		backgroundColor === "transparent" ||
		backgroundColor === "rgba(0, 0, 0, 0)"
	) {
		return "";
	}

	return backgroundColor;
}

function syncActiveNavButtonTextColor(btn, section, root, isActive) {
	if (!isActive) {
		btn.style.removeProperty("--paaadel-btn-color");
		return;
	}

	if (document.documentElement.classList.contains("is-high-contrast")) {
		btn.style.setProperty("--paaadel-btn-color", "#000000");
		return;
	}

	const pageBg = getSlidePageBackgroundColor(
		getNavSectionSlide(root, section),
	);

	if (pageBg) {
		btn.style.setProperty("--paaadel-btn-color", pageBg);
	} else {
		btn.style.removeProperty("--paaadel-btn-color");
	}
}

function getFooterBarHeightPx(root) {
	const bar = root.querySelector(
		".fullpage-slide--footer .fullpage-slide__footer-bar",
	);
	if (!bar) {
		return 0;
	}

	return Math.round(bar.getBoundingClientRect().height);
}

function isFooterSlideActive(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(slide?.classList.contains("fullpage-slide--footer"));
}

function isLocationsSlideActive(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(slide?.classList.contains("fullpage-slide--locations"));
}

function isLocationsStackedLayout() {
	return window.matchMedia(LOCATIONS_STACKED_MQ).matches;
}

// Stacked layout: lift Book Now to float a grid-margin above the green area
// (the textbox's top edge), like the footer floats above the footer bar. The
// green box overlaps up onto the photo, so its top — not the photo bottom — is
// the visible green edge. Measured relative to the slide top so the value stays
// correct while the slide is still translating during a swipe.
function getLocationsBookNowOffsetPx(root, swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	const textbox = slide?.querySelector(".fullpage-slide__locations-textbox");
	if (!slide || !textbox) {
		return null;
	}

	const slideRect = slide.getBoundingClientRect();
	const boxRect = textbox.getBoundingClientRect();
	if (boxRect.height <= 0) {
		return null;
	}

	const margin = getGridMarginPx();
	const greenTopFromSlideTop = boxRect.top - slideRect.top;
	const offset = getViewportHeight() - greenTopFromSlideTop + margin;

	return Math.max(Math.round(offset), margin);
}

function activeSlideHasBgPhoto(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(slide?.classList.contains("has-slide-image"));
}

function activeSlideIsMediaSlide(swiper) {
	const slide = swiper.slides[swiper.activeIndex];
	return Boolean(slide?.classList.contains("fullpage-slide--media"));
}

function activeSlideUsesBlackBookNowText(swiper) {
	return activeSlideIsMediaSlide(swiper) || isFooterSlideActive(swiper);
}

function updateBgPhotoGlass(root, swiper) {
	root.classList.toggle("fullpage--bg-photo", activeSlideHasBgPhoto(swiper));
}

function updateBookNowButton(root, swiper) {
	const btn = root.querySelector("[data-fullpage-book-now]");
	if (!btn) {
		return;
	}

	const margin = getGridMarginPx();
	let offset = margin;

	if (isLocationsStackedLayout() && isLocationsSlideActive(swiper)) {
		const locationsOffset = getLocationsBookNowOffsetPx(root, swiper);
		offset = locationsOffset != null ? locationsOffset : margin;
	} else if (isFooterSlideActive(swiper)) {
		offset = margin + getFooterBarHeightPx(root);
	}

	btn.style.top = "auto";
	btn.style.setProperty("--paaadel-book-now-offset", `${offset}px`);

	if (document.documentElement.classList.contains("is-high-contrast")) {
		btn.style.setProperty("--paaadel-btn-color", "#000000");
		btn.style.setProperty("--paaadel-btn-color-hover", "#000000");
		btn.style.setProperty("--paaadel-btn-focus-color", "#000000");
		return;
	}

	if (activeSlideUsesBlackBookNowText(swiper)) {
		btn.style.setProperty("--paaadel-btn-color", "#000000");
		btn.style.setProperty("--paaadel-btn-color-hover", "#000000");
		btn.style.setProperty("--paaadel-btn-focus-color", "#000000");
		return;
	}

	const pageBg = getSlidePageBackgroundColor(
		swiper.slides[swiper.activeIndex],
	);

	if (pageBg) {
		btn.style.setProperty("--paaadel-btn-color", pageBg);
		btn.style.setProperty("--paaadel-btn-color-hover", pageBg);
		btn.style.setProperty("--paaadel-btn-focus-color", pageBg);
	} else {
		btn.style.removeProperty("--paaadel-btn-color");
		btn.style.removeProperty("--paaadel-btn-color-hover");
		btn.style.removeProperty("--paaadel-btn-focus-color");
	}
}

function updateSideNav(root, swiper) {
	const activeIndex = swiper.activeIndex;
	const mobile = isMobileLayout();
	const navSections = getNavSections(root);
	const bottomOrder = [...navSections].reverse();
	let topSlot = 0;

	navSections.forEach((section) => {
		const btn = root.querySelector(
			`.fullpage__side-nav [data-fullpage-tab="${section}"]`,
		);
		if (!btn) {
			return;
		}

		const slideIndex = slideIndexForSection(root, swiper, section);
		const isActive = slideIndex === activeIndex;

		if (mobile) {
			btn.dataset.navPosition = "top";
			btn.style.removeProperty("top");
			btn.style.removeProperty("bottom");
		} else {
			const atTop = slideIndex >= 0 && activeIndex >= slideIndex;
			const btnHeight = btn.offsetHeight || 36;

			if (atTop) {
				btn.dataset.navPosition = "top";
				btn.style.top = `${navTopPx(true, topSlot, btnHeight)}px`;
				topSlot += 1;
			} else {
				btn.dataset.navPosition = "bottom";
				const bottomSlot = bottomOrder.indexOf(section);
				btn.style.top = `${navTopPx(false, bottomSlot, btnHeight)}px`;
			}

			btn.style.bottom = "auto";
		}

		btn.classList.toggle("is-active", isActive);
		btn.setAttribute("aria-current", isActive ? "true" : "false");
		syncActiveNavButtonTextColor(btn, section, root, isActive);
	});

	root.querySelectorAll(".fullpage__mobile-menu-link").forEach((link) => {
		const section = link.getAttribute("data-fullpage-tab");
		if (!section) {
			return;
		}

		const slideIndex = slideIndexForSection(root, swiper, section);
		const isActive = slideIndex === activeIndex;

		link.classList.toggle("is-active", isActive);
		link.setAttribute("aria-current", isActive ? "true" : "false");
	});

	updateMobileMenuBar(root, swiper);
	updateMobileNavReserve(root);
	updateBookNowButton(root, swiper);
	updateBgPhotoGlass(root, swiper);
}

function setPageUrl(hash, { push = false } = {}) {
	const base = `${window.location.pathname}${window.location.search}`;
	const nextUrl = hash ? `${base}${hash}` : base;
	const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

	if (currentUrl === nextUrl) {
		return;
	}

	const state = { fullpage: hash || null };

	if (push) {
		history.pushState(state, "", nextUrl);
	} else {
		history.replaceState(state, "", nextUrl);
	}
}

export function initFullpage() {
	const root = document.querySelector("[data-fullpage]");
	if (!root) {
		return;
	}

	const swiperEl = root.querySelector(".fullpage__swiper");
	if (!swiperEl) {
		return;
	}

	const prefersReducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	const motionSpeed = prefersReducedMotion ? 0 : 700;
	const navMoveDuration = prefersReducedMotion ? "0ms" : `${motionSpeed}ms`;

	root.style.setProperty("--paaadel-nav-move-duration", navMoveDuration);
	let syncingFromHash = false;
	const initialHash = window.location.hash;
	const initialParsed = parsePageHash(initialHash);
	const initialIndex = initialSlideIndex(swiperEl, initialHash);
	const viewportHeight = getViewportHeight();

	applySlideHeights(swiperEl, viewportHeight);

	let swiper;

	initSwiperNestedScroll(
		() => swiper,
		() => {
			const slide = swiper?.slides?.[swiper.activeIndex];
			return slide?.querySelector(".fullpage-slide__inner") ?? null;
		},
	);

	const desktopWheel = prefersFinePointer();

	swiper = new Swiper(swiperEl, {
		modules: [Mousewheel, Pagination, Keyboard, A11y],
		direction: "vertical",
		slidesPerView: 1,
		spaceBetween: 0,
		height: viewportHeight,
		roundLengths: false,
		nested: true,
		noSwipingClass: "swiper-no-swiping",
		touchStartPreventDefault: false,
		touchStartForcePreventDefault: false,
		resizeObserver: true,
		observer: true,
		observeParents: true,
		initialSlide: initialIndex,
		speed: motionSpeed,
		mousewheel: {
			forceToWheel: false,
			releaseOnEdges: true,
			sensitivity: desktopWheel ? 1.4 : 1.15,
			thresholdDelta: desktopWheel ? 7 : 14,
			thresholdTime: desktopWheel ? 160 : 280,
		},
		pagination: {
			el: root.querySelector(".swiper-pagination"),
			clickable: true,
		},
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		a11y: {
			enabled: true,
		},
	});

	bindSlideInnerWheelScroll(swiperEl, swiper);
	initHeroScrollHint(swiper);
	syncSlideInnerSwiping(root);

	snapSwiperToIndex(swiper, initialIndex, 0);
	requestAnimationFrame(() =>
		snapSwiperToIndex(swiper, swiper.activeIndex, 0),
	);

	const goToHash = (hash, { push = false } = {}) => {
		const index = indexForHash(swiper, hash);
		if (index < 0) {
			return;
		}

		syncingFromHash = true;
		snapSwiperToIndex(swiper, index, motionSpeed);
		setPageUrl(hashForSlide(swiper.slides[index]), { push });
		syncingFromHash = false;
		updateSideNav(root, swiper);
	};

	const syncUrlFromActiveSlide = ({ push = false } = {}) => {
		if (syncingFromHash) {
			return;
		}

		const slide = swiper.slides[swiper.activeIndex];
		setPageUrl(hashForSlide(slide), { push });
	};

	const onSlideChange = () => {
		const slide = swiper.slides[swiper.activeIndex];
		const section = normalizeHashId(
			slide?.getAttribute("data-fullpage-section") || slide?.id || "",
		);

		slide?.querySelector(".fullpage-slide__inner")?.scrollTo?.(0, 0);

		// Events + locations slide URLs are managed by their inner widgets.
		if (section !== EVENTS_SECTION && section !== LOCATIONS_SECTION) {
			syncUrlFromActiveSlide();
		}

		updateSideNav(root, swiper);
	};

	if (!initialParsed.eventDate && !initialParsed.locationId) {
		syncUrlFromActiveSlide();
	}
	updateSideNav(root, swiper);
	swiper.on("slideChange", onSlideChange);
	swiper.on("slideChangeTransitionStart", () => {
		const slide = swiper.slides[swiper.activeIndex];
		const previousSlide = swiper.slides[swiper.previousIndex];
		const section = normalizeHashId(
			slide?.getAttribute("data-fullpage-section") || slide?.id || "",
		);
		const previousSection = normalizeHashId(
			previousSlide?.getAttribute("data-fullpage-section") ||
				previousSlide?.id ||
				"",
		);

		document.dispatchEvent(
			new CustomEvent("paaadel:fullpage-slide-change", {
				detail: { section, previousSection },
			}),
		);
	});
	swiper.on("slideChangeTransitionEnd", () => {
		snapSwiperToIndex(swiper, swiper.activeIndex, 0);
	});

	const onViewportChange = () => {
		syncFullpageViewport(root, swiperEl, swiper);
		syncSlideInnerSwiping(root);
		updateSideNav(root, swiper);
	};

	window.addEventListener("resize", onViewportChange);
	window.visualViewport?.addEventListener("resize", onViewportChange);
	swiper.on("resize", () => syncFullpageViewport(root, swiperEl, swiper));

	window.addEventListener("hashchange", () => {
		if (syncingFromHash) {
			return;
		}
		goToHash(window.location.hash);
	});

	window.addEventListener("popstate", () => {
		goToHash(window.location.hash);
	});

	root.querySelectorAll("[data-fullpage-tab]").forEach((tab) => {
		tab.addEventListener("click", (event) => {
			event.preventDefault();
			const href = tab.getAttribute("href");
			if (href && href.startsWith("#")) {
				goToHash(href, { push: true });
				return;
			}

			const section = tab.getAttribute("data-fullpage-tab");
			const index = slideIndexForSection(root, swiper, section);
			if (index >= 0) {
				const slide = swiper.slides[index];
				syncingFromHash = true;
				snapSwiperToIndex(swiper, index, motionSpeed);
				setPageUrl(hashForSlide(slide), { push: true });
				syncingFromHash = false;
				updateSideNav(root, swiper);
			}
		});
	});

	bindMobileMenu(root, swiper, goToHash);
}
